import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, subDays } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { Check, Pencil, Trash2, Trophy, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import WheelPickerSheet from './WheelPickerSheet';
import { ClimbLogDialog, type ClimbLogFormData, type ClimbLogSaveOutcome } from '../climb-log';
import { CragSupportDialog } from '../crags/CragSupportDialog';
import { attemptsService, type Attempt } from '../../services/attempts';
import { climbLogsService } from '../../services/climb-logs';
import { commentsService } from '../../services/comments';
import { useAuth } from '../../hooks/useAuth';
import { useConfetti } from '../../hooks/useConfetti';
import { trackEvent } from '../../utils/analytics';
import { usePushNudgeDialog } from '../push/PushNudgeDialog';
import {
  markCragSupportPrompted,
  shouldPromptForCragSupport,
} from '../../utils/crag-support-prompt';
import type { ClimbLog, SendType, ClimbStyleType } from '../../types/climb-log';
import type { CragSupportPrompt } from '../../types/maintainer';

interface SendTrophyBadgeProps {
  climbSlug: string;
  difficulty?: string | null;
  /**
   * Earliest day a send or an attempt may be dated, `yyyy-MM-dd`; undefined for
   * no floor, which is the outdoor case. See `logDateFloor`.
   */
  minDate?: string;
  climbType?: 'Boulder' | 'Route';
  onClimbLogChange?: (log: ClimbLog | null) => void;
  onAttemptsCountChange?: (count: number) => void;
  onSendsRefresh?: () => void;
  onRatingSaved?: () => void;
  /** Increment to open the send dialog in new mode (e.g. from FAB). */
  addTopTrigger?: number;
  /** Increment to open the send dialog in edit mode. */
  editSendTrigger?: number;
  /** Increment to create a 'try' attempt (e.g. from FAB). */
  addTryTrigger?: number;
  /** Increment to open the list of this user's attempts on the climb. */
  openAttemptsTrigger?: number;
  /** Increment to open the rate-only dialog (stars + grade opinion + climb style). */
  openRateTrigger?: number;
  /** Location slug used to scope the send-date quick picker preference. */
  dateScope?: string | null;
  /**
   * Render the dialogs only, without the trophy/attempts badge. For callers
   * that already have their own way in — the crag topo's per-route button —
   * and want the send, tries and attempts flows behind it rather than a second
   * control next to it.
   */
  hideBadge?: boolean;
  className?: string;
}

const SEND_TYPE_LABEL_KEY: Record<SendType, string> = {
  on_sight: 'climbLog.onSight',
  flash: 'climbLog.flash',
  worked: 'climbLog.worked',
  nd: 'climbLog.nd',
};

type TrophyTier = 'gold' | 'silver' | 'bronze' | 'empty';

function getTrophyTier(isSent: boolean, sendType: SendType | null): TrophyTier {
  if (!isSent) return 'empty';
  switch (sendType) {
    case 'on_sight':
    case 'flash':
      return 'gold';
    case 'worked':
      return 'silver';
    case 'nd':
      return 'bronze';
    default:
      return 'gold';
  }
}

const TROPHY_CLASS: Record<TrophyTier, string> = {
  gold: 'text-yellow-500 fill-yellow-400',
  silver: 'text-slate-400 fill-slate-300',
  bronze: 'text-amber-700 fill-amber-600',
  empty: 'text-muted-foreground',
};

/**
 * How long to wait after the send form closes before asking about the crag's
 * maintainers — long enough for the form's own close animation to finish.
 */
const CRAG_SUPPORT_DELAY_MS = 350;

/**
 * How far the "how many tries" wheel runs. A session on one problem is counted
 * in goes rather than in dozens, but the wheel has to end somewhere.
 */
const TRIES_WHEEL_MAX = 30;

const TRIES_OPTIONS = Array.from({ length: TRIES_WHEEL_MAX }, (_, index) => ({
  value: index + 1,
  label: String(index + 1),
}));

/**
 * A date input only opens its calendar from the small icon at the edge, which
 * is a poor target on a phone — anywhere on the field should do it. Not every
 * browser has showPicker, and it refuses outside a user gesture, so both are
 * shrugged off: the field still types.
 */
const openDatePicker = (event: { currentTarget: HTMLInputElement }) => {
  const input = event.currentTarget;
  if (typeof input.showPicker !== 'function') return;
  try {
    input.showPicker();
  } catch {
    /* not allowed here — the field stays editable by hand */
  }
};

const ATTEMPT_TYPE_BADGE_CLASS: Record<string, string> = {
  top: 'bg-green-100 text-green-800',
  zone: 'bg-yellow-100 text-yellow-800',
  try: 'bg-blue-100 text-blue-800',
};

export default function SendTrophyBadge({
  climbSlug,
  difficulty,
  minDate,
  climbType,
  onClimbLogChange,
  onAttemptsCountChange,
  onSendsRefresh,
  onRatingSaved,
  addTopTrigger,
  editSendTrigger,
  addTryTrigger,
  openAttemptsTrigger,
  openRateTrigger,
  dateScope,
  hideBadge = false,
  className = '',
}: SendTrophyBadgeProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const { celebrate } = useConfetti();

  const [climbLog, setClimbLog] = useState<ClimbLog | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [editingSend, setEditingSend] = useState(false);
  // The comment this user logged the send with, when there is one. Held so the
  // form can show it back and save over it, rather than leaving a second one.
  const [sendComment, setSendComment] = useState<{ id: string; body: string } | null>(null);
  const [editingDateAttemptId, setEditingDateAttemptId] = useState<string | null>(null);
  const [editingDateValue, setEditingDateValue] = useState('');
  const [attemptToDelete, setAttemptToDelete] = useState<Attempt | null>(null);
  const [deletingAttemptId, setDeletingAttemptId] = useState<string | null>(null);
  // "How many tries, and when" sheet — opened instead of silently logging one.
  const [triesPickerOpen, setTriesPickerOpen] = useState(false);
  const [triesDate, setTriesDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [removeSendPromptReason, setRemoveSendPromptReason] = useState<'no_more_tops' | 'below_count' | null>(null);
  // Who looks after the crag this send was logged at, once a day at most. It
  // waits in the ref until the send form is off the screen — see below.
  const [cragSupport, setCragSupport] = useState<CragSupportPrompt | null>(null);
  const pendingCragSupport = useRef<CragSupportPrompt | null>(null);
  const cragSupportTimer = useRef<number | undefined>(undefined);
  // A first top asks to turn push on — unless the crag's ask is due, which is
  // rarer and time-bound. Held and released the same way.
  const pushNudge = usePushNudgeDialog('send');
  const pendingPushNudge = useRef(false);

  useEffect(() => () => window.clearTimeout(cragSupportTimer.current), []);

  const onClimbLogChangeRef = useRef(onClimbLogChange);
  onClimbLogChangeRef.current = onClimbLogChange;
  const onAttemptsCountChangeRef = useRef(onAttemptsCountChange);
  onAttemptsCountChangeRef.current = onAttemptsCountChange;

  // A trigger that arrives before the log and attempts have loaded — from a
  // caller that mounts this and asks in the same breath, as a list row does —
  // is held here and run once they land. Opened at once, the send form would
  // read a climb already sent as a blank new send (it takes its initial values
  // on open), and the tries it counts from would be missing. A ref rather than
  // a `loaded` dependency on the trigger effects, so a later reload (the user
  // object changing) cannot re-run a trigger already spent.
  const loadedRef = useRef(false);
  const heldTriggerRef = useRef<(() => void) | null>(null);
  const onceLoaded = (run: () => void) => {
    if (loadedRef.current) run();
    else heldTriggerRef.current = run;
  };

  // Load climb log + attempts on mount (and when climbSlug changes)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const [log, userAttempts] = await Promise.all([
          climbLogsService.getClimbLog(climbSlug).catch(() => null),
          attemptsService.getClimbAttempts(climbSlug, user.slug).catch(() => [] as Attempt[]),
        ]);
        if (cancelled) return;
        setClimbLog(log);
        setAttempts(userAttempts);
      } catch (error) {
        console.error('Failed to load trophy badge data:', error);
      } finally {
        if (!cancelled) {
          loadedRef.current = true;
          const held = heldTriggerRef.current;
          heldTriggerRef.current = null;
          held?.();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [climbSlug, isAuthenticated, user]);

  // Held back while the send form is up: the caller may answer a fresh top by
  // taking the row away (a list showing only what is not sent yet) or by
  // starting the first-top tour, and either would land on the stars the form
  // asks for after the save. Told once it closes, as it always was.
  useEffect(() => {
    if (showSendDialog) return;
    onClimbLogChangeRef.current?.(climbLog);
  }, [climbLog, showSendDialog]);

  // What this user already said under the send being edited. Read when the form
  // opens rather than with the log, so a climb page nobody logs a send on pays
  // nothing for it; the form takes it late (see ClimbLogDialog's initialComment)
  // and a box already typed in keeps what is in it.
  useEffect(() => {
    if (!showSendDialog || !editingSend || !climbLog?.id || !user) return;
    let cancelled = false;

    commentsService.getComments(climbLog.id)
      .then((res) => {
        if (cancelled) return;
        const own = res.data.find((comment) => comment.user.id === user.id.toString());
        if (own) setSendComment({ id: own.id, body: own.body });
      })
      .catch(() => {
        /* a comment that cannot be read is no reason to hold up the form */
      });

    return () => {
      cancelled = true;
    };
  }, [showSendDialog, editingSend, climbLog?.id, user]);

  const isSent = !!climbLog?.topped;
  const sendType = climbLog?.send_type ?? null;
  const displayCount = Math.max(attempts.length, climbLog?.attempts_count ?? 0);

  useEffect(() => {
    onAttemptsCountChangeRef.current?.(displayCount);
  }, [displayCount]);

  // The send's own comment — the first thing said under it, and where a tagged
  // partner gets notified from (a Comment is what carries the mention).
  //
  // An edit saves over the same comment instead of adding another, and clearing
  // the box leaves what is there: deleting somebody's words from a form that
  // does not show them being deleted would be a surprise.
  const saveSendComment = async (ascentLogId: string, body?: string) => {
    const text = body?.trim();
    if (!text) return;
    if (sendComment && text === sendComment.body) return;

    try {
      const res = sendComment
        ? await commentsService.updateComment(ascentLogId, sendComment.id, text)
        : await commentsService.createComment(ascentLogId, text);
      setSendComment({ id: res.data.id, body: res.data.body });
    } catch (error) {
      // The send itself is already saved, so this says what is actually wrong
      // rather than letting the send's own error swallow it.
      console.error('Failed to save the send comment:', error);
      toast({ title: t('comments.saveError'), variant: 'destructive' });
    }
  };

  // A fresh send starts with an empty box; editing one fills it from the effect
  // below, which is what also makes the save go over that comment rather than
  // leaving a second one under the same send.
  const openSendDialog = (editing: boolean) => {
    setEditingSend(editing);
    setSendComment(null);
    setShowSendDialog(true);
  };

  // External trigger: open send dialog (new)
  useEffect(() => {
    if (!addTopTrigger || !isAuthenticated) return;
    onceLoaded(() => openSendDialog(false));
  }, [addTopTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // External trigger: open send dialog (edit)
  useEffect(() => {
    if (!editSendTrigger || !isAuthenticated) return;
    onceLoaded(() => openSendDialog(true));
  }, [editSendTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // External trigger: log tries. A session is counted in goes on a given day,
  // so the sheet asks for both instead of the button standing for exactly one.
  useEffect(() => {
    if (!addTryTrigger || !isAuthenticated) return;
    onceLoaded(() => {
      setTriesDate(format(new Date(), 'yyyy-MM-dd'));
      setTriesPickerOpen(true);
    });
  }, [addTryTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // External trigger: open the attempts drawer — the same one the badge's own
  // count opens, for callers that show the count somewhere else.
  useEffect(() => {
    if (!openAttemptsTrigger || !isAuthenticated) return;
    onceLoaded(() => setDrawerOpen(true));
  }, [openAttemptsTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // External trigger: open rate dialog
  useEffect(() => {
    if (!openRateTrigger || !isAuthenticated) return;
    onceLoaded(() => setShowRateDialog(true));
  }, [openRateTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated || !user) return null;

  const dateLocale = i18n.language === 'it' ? it : enUS;
  const tier = getTrophyTier(isSent, sendType);
  const todayValue = format(new Date(), 'yyyy-MM-dd');
  const yesterdayValue = format(subDays(new Date(), 1), 'yyyy-MM-dd');

  const createTryAttempts = async (count: number) => {
    const attemptedAt = triesDate;
    try {
      const created: Attempt[] = [];
      for (let i = 0; i < count; i++) {
        const result = await attemptsService.createAttempt(climbSlug, {
          attempt_type: 'try',
          attempted_at: attemptedAt,
        });
        created.push(result.attempt);
      }
      setAttempts(prev => [...prev, ...created]);
      trackEvent('attempt_logged', { climb_slug: climbSlug, attempt_type: 'try', count });
      toast({ title: t('attempts.createSuccessCount', { count }) });
      onSendsRefresh?.();
    } catch (error) {
      console.error('Failed to create attempts:', error);
      toast({ title: t('attempts.createError'), variant: 'destructive' });
    }
  };

  const handleTrophyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openSendDialog(isSent);
  };

  const handleAttemptsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDrawerOpen(true);
  };

  const sortedAttempts = attempts.slice().sort((a, b) =>
    new Date(b.attempted_at).getTime() - new Date(a.attempted_at).getTime()
  );

  const nonTopCount = attempts.filter(a => a.attempt_type !== 'top').length;
  const topCount = attempts.filter(a => a.attempt_type === 'top').length;

  const defaultFreshSendType: SendType = nonTopCount > 0
    ? 'worked'
    : climbType === 'Boulder' ? 'flash' : 'on_sight';

  const sendDialogInitialValues = editingSend && climbLog
    ? {
        send_type: climbLog.send_type as SendType | null,
        attempts_count: climbLog.attempts_count,
        topped_at: climbLog.topped_at,
        grade_opinion: climbLog.grade_opinion ?? null,
        rating: climbLog.rating ?? null,
        climb_style: (climbLog.climb_style as ClimbStyleType[]) ?? [],
      }
    : {
        send_type: defaultFreshSendType,
        attempts_count: nonTopCount > 0 ? nonTopCount + 1 : 1,
        topped_at: null,
        grade_opinion: climbLog?.grade_opinion ?? null,
        climb_style: (climbLog?.climb_style as ClimbStyleType[]) ?? [],
      };

  const handleSendSave = async (data: ClimbLogFormData): Promise<ClimbLogSaveOutcome> => {
    const isNd = data.send_type === 'nd';
    const isWorked = data.send_type === 'worked';
    const wasNotTopped = !climbLog?.topped;

    try {
      const toppedAtIso = data.topped_at
        ? new Date(data.topped_at).toISOString()
        : new Date().toISOString();
      const updateData = {
        topped: true,
        topped_at: toppedAtIso,
        send_type: data.send_type ?? null,
        attempts_count: isWorked && data.attempts_count ? data.attempts_count : null,
        grade_opinion: data.grade_opinion ?? null,
        climb_style: data.climb_style ?? [],
        // Only when the form had stars and they were touched: a send never
        // votes on the climber's behalf, nor writes over a vote already given.
        ...(data.rating !== undefined ? { rating: data.rating } : {}),
      };
      const { log: updatedLog, cragSupport: support } = await climbLogsService.createOrUpdate(
        climbSlug,
        updateData
      );
      setClimbLog(updatedLog);

      if (!isNd) {
        const dateStr = data.topped_at
          ? format(new Date(data.topped_at), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd');
        const targetTotal = isWorked && data.attempts_count
          ? data.attempts_count
          : attempts.length + (topCount === 0 ? 1 : 0);
        const totalDelta = Math.max(0, targetTotal - attempts.length);
        const newTopCount = topCount === 0 ? 1 : 0;
        const newTryCount = Math.max(0, totalDelta - newTopCount);

        const created: Attempt[] = [];
        for (let i = 0; i < newTryCount; i++) {
          const result = await attemptsService.createAttempt(climbSlug, {
            attempt_type: 'try',
            attempted_at: dateStr,
          });
          created.push(result.attempt);
        }
        for (let i = 0; i < newTopCount; i++) {
          const result = await attemptsService.createAttempt(climbSlug, {
            attempt_type: 'top',
            attempted_at: dateStr,
          });
          created.push(result.attempt);
        }
        if (created.length > 0) {
          setAttempts(prev => [...prev, ...created]);
        }
      }

      await saveSendComment(updatedLog.id, data.comment);

      trackEvent('send_saved', {
        send_type: data.send_type,
        is_nd: isNd,
        is_first_top: wasNotTopped && !isNd,
      });
      toast({ title: t('climbLog.saveSuccess') });
      onSendsRefresh?.();
      onRatingSaved?.();

      if (wasNotTopped) {
        celebrate();
      }

      // The bolts on that route were paid for by somebody. Asked after the
      // confetti rather than instead of it, and only once a day per crag.
      //
      // Held rather than shown: this runs inside the send form's own save, so
      // the form is still up. It is released once that closes.
      if (support && shouldPromptForCragSupport(support.crag.slug)) {
        markCragSupportPrompted(support.crag.slug);
        pendingCragSupport.current = support;
        trackEvent('crag_support_prompted', { crag_slug: support.crag.slug });
      } else if (wasNotTopped) {
        pendingPushNudge.current = true;
      }

      // The first top of a climb not voted on yet: the form stays up, under
      // the confetti, and asks for the stars on their own.
      return wasNotTopped && updatedLog.rating == null ? 'ask_rating' : 'done';
    } catch (error) {
      console.error('Error saving send:', error);
      toast({ title: t('climbLog.saveError'), variant: 'destructive' });
      return 'done';
    }
  };

  const handleSendRate = async (rating: number) => {
    try {
      const { log: updatedLog } = await climbLogsService.createOrUpdate(climbSlug, { rating });
      setClimbLog(updatedLog);
      onRatingSaved?.();
      onSendsRefresh?.();
    } catch (error) {
      console.error('Error saving rating:', error);
      toast({ title: t('climbLog.saveError'), variant: 'destructive' });
    }
  };

  // Puts the held ask on screen, once the send form has finished closing.
  //
  // Opening it in the same breath as the close would leave two dialogs in
  // flight, which is how Radix ends up leaving its scroll lock on the body —
  // and it would read as the ask being part of the form rather than a thank you
  // for the send.
  const releasePendingCragSupport = () => {
    const pending = pendingCragSupport.current;
    if (!pending) {
      releasePendingPushNudge();
      return;
    }

    pendingCragSupport.current = null;
    cragSupportTimer.current = window.setTimeout(
      () => setCragSupport(pending),
      CRAG_SUPPORT_DELAY_MS
    );
  };

  const releasePendingPushNudge = () => {
    if (!pendingPushNudge.current) return;

    pendingPushNudge.current = false;
    cragSupportTimer.current = window.setTimeout(() => pushNudge.maybeOpen(), CRAG_SUPPORT_DELAY_MS);
  };

  const handleRateSave = async (data: ClimbLogFormData) => {
    try {
      const { log: updatedLog } = await climbLogsService.createOrUpdate(climbSlug, {
        rating: data.rating,
        grade_opinion: data.grade_opinion ?? null,
        climb_style: data.climb_style ?? [],
      });
      setClimbLog(updatedLog);
      toast({ title: t('climbLog.saveSuccess') });
      onRatingSaved?.();
      onSendsRefresh?.();
    } catch (error) {
      console.error('Error saving rating:', error);
      toast({ title: t('climbLog.saveError'), variant: 'destructive' });
    }
  };

  const handleRemoveSend = async () => {
    try {
      const updatedLog = await climbLogsService.update(climbSlug, {
        send_type: null,
        attempts_count: null,
        topped: false,
        topped_at: null,
      });
      setClimbLog(updatedLog);
      toast({ title: t('climbLog.removeSendSuccess') });
      onSendsRefresh?.();
    } catch (error) {
      console.error('Error removing send:', error);
      toast({ title: t('climbLog.saveError'), variant: 'destructive' });
    }
  };

  const handleStartEditDate = (attempt: Attempt) => {
    setEditingDateAttemptId(attempt.id);
    setEditingDateValue(format(new Date(attempt.attempted_at), 'yyyy-MM-dd'));
  };

  const handleSaveDate = async (attempt: Attempt) => {
    try {
      const newDate = new Date(editingDateValue);
      const original = new Date(attempt.attempted_at);
      newDate.setHours(original.getHours(), original.getMinutes(), original.getSeconds());

      const updated = await attemptsService.updateAttempt(climbSlug, attempt.id, {
        attempted_at: newDate.toISOString(),
      });
      setAttempts(prev => prev.map(a => (a.id === attempt.id ? updated : a)));
      toast({ title: t('attempts.updateSuccess') });
      onSendsRefresh?.();
    } catch (error) {
      console.error('Error updating attempt date:', error);
      toast({ title: t('attempts.updateError'), variant: 'destructive' });
    } finally {
      setEditingDateAttemptId(null);
    }
  };

  const confirmDeleteAttempt = async () => {
    if (!attemptToDelete) return;
    const deleted = attemptToDelete;
    try {
      setDeletingAttemptId(deleted.id);
      await attemptsService.deleteAttempt(climbSlug, deleted.id);
      const remaining = attempts.filter(a => a.id !== deleted.id);
      setAttempts(remaining);
      toast({ title: t('attempts.deleteSuccess') });
      onSendsRefresh?.();

      if (climbLog?.topped) {
        const remainingTopCount = remaining.filter(a => a.attempt_type === 'top').length;
        if (deleted.attempt_type === 'top' && remainingTopCount === 0) {
          setRemoveSendPromptReason('no_more_tops');
        } else if (climbLog.attempts_count != null && remaining.length < climbLog.attempts_count) {
          setRemoveSendPromptReason('below_count');
        }
      }
    } catch (error) {
      console.error('Error deleting attempt:', error);
      toast({ title: t('attempts.deleteError'), variant: 'destructive' });
    } finally {
      setDeletingAttemptId(null);
      setAttemptToDelete(null);
    }
  };

  return (
    <>
      {!hideBadge && (
        <div className={`inline-flex items-center rounded border border-border bg-background/80 overflow-hidden text-sm font-medium ${className}`}>
          <button
            type="button"
            onClick={handleTrophyClick}
            title={isSent ? t('sendTrophy.editTooltip') : t('sendTrophy.addTooltip')}
            className="px-2 py-0.5 hover:bg-muted transition-colors"
          >
            <Trophy className={`w-4 h-4 ${TROPHY_CLASS[tier]}`} />
          </button>
          <button
            type="button"
            onClick={handleAttemptsClick}
            title={t('sendTrophy.attemptsTooltip')}
            className="px-2 py-0.5 hover:bg-muted transition-colors text-muted-foreground border-l border-border"
          >
            {displayCount}
          </button>
        </div>
      )}

      <ClimbLogDialog
        open={showRateDialog}
        onOpenChange={setShowRateDialog}
        mode="rate"
        difficulty={difficulty}
        initialValues={{
          rating: climbLog?.rating ?? null,
          grade_opinion: climbLog?.grade_opinion ?? null,
          climb_style: (climbLog?.climb_style as ClimbStyleType[]) ?? [],
        }}
        onSave={handleRateSave}
      />

      <ClimbLogDialog
        open={showSendDialog}
        onOpenChange={(open) => {
          setShowSendDialog(open);
          if (!open) {
            setEditingSend(false);
            releasePendingCragSupport();
          }
        }}
        mode="send"
        difficulty={difficulty}
        climbType={climbType}
        initialValues={sendDialogInitialValues}
        initialComment={sendComment?.body}
        onSave={handleSendSave}
        onRate={handleSendRate}
        showRatingInSend={editingSend}
        showDelete={editingSend && !!climbLog?.topped}
        onDelete={handleRemoveSend}
        existingAttemptsCount={attempts.length}
        topAlreadyLogged={topCount > 0}
        minDate={minDate}
        dateScope={dateScope}
      />

      {/* Tries are logged in one go: the wheel says how many, the date says when. */}
      <WheelPickerSheet
        open={triesPickerOpen}
        onOpenChange={setTriesPickerOpen}
        label={t('attempts.howManyTries')}
        value={1}
        options={TRIES_OPTIONS}
        onChange={(count) => void createTryAttempts(count)}
        extra={
          <div className="space-y-2">
            <Label htmlFor="tries-date">{t('attempts.attemptDate')}</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                id="tries-date"
                type="date"
                value={triesDate}
                min={minDate ?? undefined}
                max={todayValue}
                onChange={(e) => setTriesDate(e.target.value)}
                onClick={openDatePicker}
                onFocus={openDatePicker}
                className="w-40"
              />
              <Button
                type="button"
                variant={triesDate === todayValue ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTriesDate(todayValue)}
              >
                {t('climbLog.today')}
              </Button>
              <Button
                type="button"
                variant={triesDate === yesterdayValue ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTriesDate(yesterdayValue)}
              >
                {t('climbLog.yesterday')}
              </Button>
            </div>
          </div>
        }
      />

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t('attempts.yourAttempts')}</SheetTitle>
          </SheetHeader>

          {/* The send (libera) surfaced at the top, easier to reach than the small trophy button */}
          {isSent && (
            <div className="mt-4 flex items-center justify-between gap-2 rounded-lg border bg-muted/40 p-3">
              <div className="flex items-center gap-2 min-w-0">
                <Trophy className={`w-5 h-5 shrink-0 ${TROPHY_CLASS[tier]}`} />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {sendType ? t(SEND_TYPE_LABEL_KEY[sendType]) : t('sendTrophy.editTooltip')}
                  </div>
                  {climbLog?.topped_at && (
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(climbLog.topped_at), 'MMM d, yyyy', { locale: dateLocale })}
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => {
                  setDrawerOpen(false);
                  setEditingSend(true);
                  setShowSendDialog(true);
                }}
              >
                <Pencil className="h-3 w-3 mr-1" />
                {t('common.edit')}
              </Button>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {sortedAttempts.length === 0 ? (
              <div className="text-center text-muted-foreground py-4">
                {t('attempts.noAttempts')}
              </div>
            ) : (
              sortedAttempts.map((attempt) => (
                <div
                  key={attempt.id}
                  className="flex items-center justify-between p-2 rounded border"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Badge
                      variant="secondary"
                      className={ATTEMPT_TYPE_BADGE_CLASS[attempt.attempt_type] ?? 'bg-gray-100 text-gray-800'}
                    >
                      {t(`attempts.${attempt.attempt_type}Attempt`)}
                    </Badge>

                    {editingDateAttemptId === attempt.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="date"
                          value={editingDateValue}
                          onChange={(e) => setEditingDateValue(e.target.value)}
                          className="h-6 text-xs w-32 py-0 px-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSaveDate(attempt)}
                          className="h-6 w-6 p-0 text-green-600"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingDateAttemptId(null)}
                          className="h-6 w-6 p-0 text-muted-foreground"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(attempt.attempted_at), 'MMM d, yyyy', { locale: dateLocale })}
                      </span>
                    )}
                  </div>

                  {editingDateAttemptId !== attempt.id && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEditDate(attempt)}
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAttemptToDelete(attempt)}
                        disabled={deletingAttemptId === attempt.id}
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!attemptToDelete} onOpenChange={(open) => { if (!open) setAttemptToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('attempts.deleteAttempt')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('attempts.confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteAttempt}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!removeSendPromptReason}
        onOpenChange={(open) => { if (!open) setRemoveSendPromptReason(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('climbLog.removeSendPromptTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {removeSendPromptReason === 'no_more_tops'
                ? t('climbLog.removeSendPromptNoMoreTopsBody')
                : t('climbLog.removeSendPromptBelowCountBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('climbLog.removeSendPromptKeep')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await handleRemoveSend();
                setRemoveSendPromptReason(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('climbLog.removeSendPromptRemove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Who keeps the crag climbable, after a send there. Renders nothing
          until a save comes back with somebody to ask about. */}
      <CragSupportDialog
        prompt={cragSupport}
        onOpenChange={(open) => { if (!open) setCragSupport(null); }}
      />

      {pushNudge.dialog}
    </>
  );
}
